import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Loader2, Briefcase, Languages, ArrowRight, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../../lib/apiError';

// iter98 (Feb 2026) — Lightweight completion step shown to WORKERS who
// signed up BEFORE the Post Office-specific onboarding landed. It asks
// only for the minimum data the matching engine needs (currentRole +
// yearsExperience + languages) so we don't force these pilot users
// through the full 5-step onboarding again. Full onboarders never see
// this — it's gated on missing workerProfile in the OnboardingGate.

const YEARS_OPTIONS = [
  { value: 'less_than_1', label: 'Less than 1 year' },
  { value: '1_to_2', label: '1–2 years' },
  { value: '3_to_5', label: '3–5 years' },
  { value: '5_to_10', label: '5–10 years' },
  { value: '10_plus', label: '10+ years' },
];

export default function PostOfficeProfileGate({ onDone }) {
  const { api, refreshUser } = useAuth();
  const [roles, setRoles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    currentRole: '',
    yearsExperience: '',
    additionalAreas: [],
    fluentEnglish: null,
    otherLanguages: [],
  });
  const [langDraft, setLangDraft] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/meta/post-office-vocab');
        setRoles(data.roles || []);
      } catch { /* silent */ }
    })();
  }, [api]);

  const setField = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    if (errors[k]) setErrors(er => ({ ...er, [k]: undefined }));
  };
  const toggleArea = (r) => setForm(f => ({
    ...f,
    additionalAreas: f.additionalAreas.includes(r) ? f.additionalAreas.filter(a => a !== r) : [...f.additionalAreas, r],
  }));
  const addLanguage = () => {
    const v = langDraft.trim();
    if (!v || form.otherLanguages.includes(v)) { setLangDraft(''); return; }
    setForm(f => ({ ...f, otherLanguages: [...f.otherLanguages, v] }));
    setLangDraft('');
  };
  const removeLanguage = (l) => setForm(f => ({ ...f, otherLanguages: f.otherLanguages.filter(x => x !== l) }));

  const validate = () => {
    const next = {};
    if (!form.currentRole) next.currentRole = 'Pick your current Post Office role.';
    if (!form.yearsExperience) next.yearsExperience = 'Pick how long you have been doing this.';
    if (form.fluentEnglish === null) next.fluentEnglish = 'Please confirm English fluency for language-specific shifts.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await api.post('/worker/post-office-profile', {
        currentPostOfficeRole: form.currentRole,
        yearsExperience: form.yearsExperience,
        additionalPostOfficeAreas: form.additionalAreas.filter(a => a !== form.currentRole),
        fluentEnglish: !!form.fluentEnglish,
        otherLanguages: form.otherLanguages,
      });
      await refreshUser();
      if (typeof onDone === 'function') onDone();
      toast.success('Post Office profile saved.');
    } catch (err) {
      toast.error(formatApiError(err, 'Could not save. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9] flex items-center justify-center p-4" data-testid="po-profile-gate">
      <Card className="w-full max-w-md border-0 shadow-lg">
        <CardContent className="p-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-11 w-11 bg-[#DA291C] rounded-xl flex items-center justify-center shrink-0">
              <Briefcase className="h-6 w-6 text-white" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Complete your Post Office profile</h2>
          </div>
          <p className="text-base text-slate-600 mb-6" data-testid="po-profile-gate-intro">
            Managers now pick cover based on Post Office experience. Take a
            minute to add your role and years of experience so you show up
            for the right shifts.
          </p>

          <div className="space-y-6">
            <div>
              <Label className="text-base font-medium text-slate-800 mb-2 block">Current Post Office role</Label>
              <select
                value={form.currentRole}
                onChange={e => setField('currentRole', e.target.value)}
                className="w-full h-12 rounded-xl border-2 border-slate-200 px-3 text-base bg-white"
                data-testid="po-gate-current-role"
              >
                <option value="">Select your current role…</option>
                {roles.map(r => (<option key={r} value={r}>{r}</option>))}
              </select>
              {errors.currentRole && (<p className="text-sm text-red-600 mt-1.5" data-testid="po-gate-current-role-error">{errors.currentRole}</p>)}
            </div>

            <div>
              <Label className="text-base font-medium text-slate-800 mb-2 block">Years of Post Office experience</Label>
              <div className="grid grid-cols-2 gap-2" data-testid="po-gate-years-group">
                {YEARS_OPTIONS.map(o => {
                  const selected = form.yearsExperience === o.value;
                  return (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => setField('yearsExperience', o.value)}
                      aria-pressed={selected}
                      className={`min-h-[48px] px-3 rounded-xl border-2 text-sm font-semibold transition-all ${selected ? 'border-[#DA291C] bg-red-50 text-[#DA291C]' : 'border-slate-200 hover:border-slate-300 text-slate-800 bg-white'}`}
                      data-testid={`po-gate-years-${o.value}`}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
              {errors.yearsExperience && (<p className="text-sm text-red-600 mt-1.5">{errors.yearsExperience}</p>)}
            </div>

            <div>
              <Label className="text-base font-medium text-slate-800 mb-2 block">
                Other Post Office services you&apos;ve done <span className="text-slate-400 font-normal">(optional)</span>
              </Label>
              <div className="flex flex-wrap gap-2" data-testid="po-gate-additional-areas">
                {roles.filter(r => r !== form.currentRole).map(r => {
                  const selected = form.additionalAreas.includes(r);
                  return (
                    <button
                      key={r}
                      type="button"
                      onClick={() => toggleArea(r)}
                      aria-pressed={selected}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${selected ? 'border-[#0f1b3d] bg-[#0f1b3d] text-white' : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'}`}
                    >
                      {r}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-base font-medium text-slate-800 mb-2 flex items-center gap-1.5">
                <Languages className="h-4 w-4 text-slate-500" /> Fluent in English?
              </Label>
              <div className="grid grid-cols-2 gap-2" data-testid="po-gate-fluent-group">
                {[{ v: true, label: 'Yes' }, { v: false, label: 'No' }].map(o => {
                  const selected = form.fluentEnglish === o.v;
                  return (
                    <button
                      key={String(o.v)}
                      type="button"
                      onClick={() => setField('fluentEnglish', o.v)}
                      aria-pressed={selected}
                      className={`min-h-[48px] rounded-xl border-2 text-base font-semibold transition-all ${selected ? 'border-[#DA291C] bg-red-50 text-[#DA291C]' : 'border-slate-200 hover:border-slate-300 text-slate-800 bg-white'}`}
                      data-testid={`po-gate-fluent-${o.v ? 'yes' : 'no'}`}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
              {errors.fluentEnglish && (<p className="text-sm text-red-600 mt-1.5">{errors.fluentEnglish}</p>)}
            </div>

            <div>
              <Label className="text-base font-medium text-slate-800 mb-2 block">
                Other languages <span className="text-slate-400 font-normal">(optional)</span>
              </Label>
              <div className="flex gap-2">
                <Input
                  value={langDraft}
                  onChange={e => setLangDraft(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addLanguage(); } }}
                  placeholder="e.g., Polish"
                  className="h-11 rounded-xl text-base"
                  data-testid="po-gate-language-input"
                />
                <Button type="button" onClick={addLanguage} variant="outline" className="h-11 rounded-xl px-3" data-testid="po-gate-language-add-btn">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {form.otherLanguages.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2" data-testid="po-gate-language-chips">
                  {form.otherLanguages.map(l => (
                    <span key={l} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-800 text-sm border border-slate-200">
                      {l}
                      <button type="button" onClick={() => removeLanguage(l)} aria-label={`Remove ${l}`} className="text-slate-500 hover:text-slate-800"><X className="h-3.5 w-3.5" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <Button
            onClick={handleSubmit}
            disabled={saving}
            className="w-full h-12 rounded-full mt-7 bg-[#DA291C] hover:bg-[#b91f13] text-white text-base font-semibold disabled:opacity-60"
            data-testid="po-gate-submit-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>Save and continue <ArrowRight className="h-4 w-4 ml-1.5" /></>)}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
